import { ROUTE_BUILDER } from '@/constants/pages';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import { isMobile, isSistaminuten, url } from '../helpers';
import { __, _t } from '../locale';

/**
 *
 * @param {string} what
 * @param {string} where
 * @param {import('@/hooks/search/useSearch.helpers').SearchType} type
 * @returns
 */
const getSERPBreadcrumbs = (what, where, type = 'default') => {
  const { title, url } = buildSerpUrl(what, where, type);
  const items = [
    {
      title: __('home'),
      url: '/',
    },
    {
      title,
      url,
    },
  ];

  return items;
};

const getKeywordFromIndustries = (industries) => {
  let what;
  if (industries?.length) {
    for (const industry of industries) {
      if (typeof industry !== 'string') {
        continue;
      }

      if (industry.includes('/')) {
        for (const part of industry.split('/')) {
          const slug = getKeywordWithSlug(part)?.slug;
          if (slug) {
            what = slug;
            break;
          }
        }
      } else {
        const slug = getKeywordWithSlug(industry)?.slug;
        if (slug) {
          what = slug;
        }
      }

      if (what) {
        break;
      }
    }
  }

  return what || DEFAULT_SEARCH_KEYWORD;
};

const getDetailsBreadcrumbs = (place, raw = false, isSistaminuten = false) => {
  const {
    about: { industries },
    contact: {
      address: { city },
    },
  } = place;

  const what = getKeywordFromIndustries(industries) || DEFAULT_SEARCH_KEYWORD;
  const where = city || DEFAULT_SEARCH_LOCATION;
  const type = isSistaminuten ? 'sistaminuten' : 'default';
  const { title, url } = buildSerpUrl(what, where, type);
  const items = [
    { title: __('home'), url: '/' },
    {
      title,
      url,
    },
    {
      title: place.about.name,
      url: encodeURI('/places/' + place.about.slug + '-' + place.id),
    },
  ];

  /**
   * @TODO - bug, will always pop last entry since isMobile a function
   * leave for now until we discuss how to refactor breadcrumbs
   */
  if (isMobile) {
    items.pop();
  }
  if (raw) {
    return items;
  }
  return items;
};

const getServiceBreadcrumbs = (place, service) => {
  const items = getDetailsBreadcrumbs(place, true);
  items.push({
    title: service.name,
    url:
      url.getBaseUrl() +
      'boka-tjanst/' +
      place?.about?.slug +
      '-' +
      place.id +
      '/' +
      service?.about?.slug +
      '-' +
      service.id,
  });
  return items;
};

const limitString = (str, length) => {
  return str ? str.replace(/(\n|\r)/gm, '').substring(0, length) : '';
};

/**
 *
 * @param {string} keyword
 * @param {string} location
 * @param {import('@/hooks/search/useSearch.helpers').SearchType} type
 * @returns {{ title: string, url: string }}
 */
const buildSerpUrl = (keyword = DEFAULT_SEARCH_KEYWORD, location = DEFAULT_SEARCH_LOCATION, type = 'default') => {
  const indexableKeyword = getKeywordWithSlug(keyword);
  const indexableLocation = getLocationWithSlug(location);
  const keywordSlug = indexableKeyword?.slug || DEFAULT_SEARCH_KEYWORD;
  const locationSlug = indexableLocation?.slug || DEFAULT_SEARCH_LOCATION;

  let title = undefined;

  if (keywordSlug !== DEFAULT_SEARCH_KEYWORD || locationSlug !== DEFAULT_SEARCH_LOCATION) {
    title = (indexableKeyword?.label || _t('Beauty&Health')) + ' ' + (indexableLocation?.label || __('allSweden'));
  } else {
    switch (type) {
      case 'sistaminuten':
        title = 'Sista Minuten';
        break;
      case 'deals_preview':
      case 'deals_sistaminuten':
      case 'deals':
        title = 'Deals';
        break;
      default:
        title = __('search');
    }
  }

  let url = undefined;

  switch (type) {
    case 'sistaminuten':
      url = ROUTE_BUILDER.sistaMinutenSerp(keywordSlug, locationSlug);
      break;
    case 'deals_preview':
      url = ROUTE_BUILDER.dealsSerpPreview(keywordSlug, locationSlug);
      break;
    case 'deals_sistaminuten':
      url = ROUTE_BUILDER.dealsSerp('sistaminuten', keywordSlug, locationSlug);
      break;
    case 'deals':
      url = ROUTE_BUILDER.dealsSerp('deals', keywordSlug, locationSlug);
      break;
    default:
      url = ROUTE_BUILDER.serp(keywordSlug, locationSlug);
  }

  return { title, url };
};

const getLocationPreposition = (location) => {
  const defaultPreposition = 'i';
  const citiesWithPaPreposition = [
    'adelsö',
    'alnö',
    'asperö',
    'björkö',
    'blidö',
    'bohus-björkö',
    'bohus-malmön',
    'bolmsö',
    'brännö',
    'dalarö',
    'donsö',
    'ekerö',
    'fotö',
    'frösön',
    'fårö',
    'granö',
    'gräddö',
    'gränö',
    'gräsö',
    'grötö',
    'gålö',
    'gällö',
    'hammarö',
    'harö',
    'holmön',
    'husarö',
    'hälsö',
    'hölö',
    'hönö',
    'ingarö',
    'ingmarsö',
    'käringön',
    'köpstadsö',
    'klädesholmen',
    'lidingö',
    'ljusterö',
    'mörkö',
    'munsö',
    'muskö',
    'nämdö',
    'ornö',
    'resö',
    'rossön',
    'runmarö',
    'rörö',
    'seskarö',
    'smögen',
    'sollenkroka ö',
    'sollerön',
    'sturkö',
    'styrsö',
    'särö',
    'tjurkö',
    'utö',
    'vikbolandet',
    'visingsö',
    'vrångö',
    'väddö',
    'värmdö',
    'vätö',
    'öckerö',
    'öland',
    'kinnekulle',
  ];
  const areaWithPaPrepositionEnding = [
    'holmen',
    'gärdet',
    'hisingen',
    'heden',
    'malm',
    'ön',
    'berget',
    'kulle',
    'gården',
  ];
  const areaWithVidPrepositionEnding = ['torget', 'station', 'platsen', 'plan'];
  if (location) {
    const trimmedLowercasedLocation = location.trim().toLowerCase();
    if (
      citiesWithPaPreposition.includes(trimmedLowercasedLocation) ||
      (trimmedLowercasedLocation.endsWith('ö') && !trimmedLowercasedLocation.endsWith('sjö')) ||
      areaWithPaPrepositionEnding.some((ending) => trimmedLowercasedLocation.endsWith(ending))
    ) {
      return 'på';
    }
    if (areaWithVidPrepositionEnding.some((ending) => trimmedLowercasedLocation.endsWith(ending))) {
      return 'vid';
    }
    return 'i';
  } else {
    return defaultPreposition;
  }
};

const getHomeDescription = () => {
  return __(isSistaminuten() ? 'seo.sistaminuten.homepageDescription' : 'seo.homepageDescription');
};

export {
  getDetailsBreadcrumbs,
  getHomeDescription,
  getLocationPreposition,
  getSERPBreadcrumbs,
  getServiceBreadcrumbs,
  limitString,
};
